import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { progressAPI, sessionAPI, vocabAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const SKILL_COLORS: Record<string, string> = {
  listening: '#004be2',
  reading: '#004be2',
  writing: '#8e00d5',
  speaking: '#004be2',
}

const SKILL_LABELS: Record<string, string> = {
  listening: 'Listening',
  reading: 'Reading',
  writing: 'Writing',
  speaking: 'Speaking',
}

const SKILL_HEIGHTS: Record<string, string> = {
  listening: '85%',
  reading: '60%',
  writing: '45%',
  speaking: '75%',
}

// snake_case error type → human label, e.g. "complex_modal_verbs" → "Complex Modal Verbs"
const humanizeFocus = (raw: string) =>
  raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export default function DashboardPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { t } = useTranslation()

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: progressAPI.dashboard,
  })

  const { data: todayStatus } = useQuery({
    queryKey: ['session-today'],
    queryFn: sessionAPI.today,
  })

  const { data: difficultyData } = useQuery({
    queryKey: ['session-difficulty'],
    queryFn: sessionAPI.difficulty,
    retry: false,
  })

  const { data: vocabData } = useQuery({
    queryKey: ['vocab-due'],
    queryFn: vocabAPI.due,
  })

  // LME-4: real-progress momentum widget (flag-gated on the server)
  const { data: momentum } = useQuery({
    queryKey: ['progress-momentum'],
    queryFn: progressAPI.momentum,
    retry: false,
  })

  const freeze = useMutation({
    mutationFn: progressAPI.freezeStreak,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard'] }),
  })

  const streak = dashboard?.streak?.current ?? 14
  const examCountdown = dashboard?.examCountdown ?? null
  const skillScores = dashboard?.learningProfile?.skills ?? {}
  const targetLevel = dashboard?.learningProfile?.targetLevel ?? null
  const forecastLevel = dashboard?.learningProfile?.forecastLevel ?? null
  // Show forecast if user chose [3] in unrealistic goal warning
  const displayLevel = forecastLevel ?? targetLevel
  const hasLearningProfile = dashboard?.learningProfile != null && (targetLevel || forecastLevel)

  // Adaptive program (FR-003/FR-006): today's focus area + critical-focus skills
  const program = dashboard?.program ?? null
  const weakSkills: string[] = program?.weakSkills ?? []
  const focusArea = program?.focusErrorType ? humanizeFocus(program.focusErrorType) : null

  const reviewWords = vocabData?.length
    ? vocabData.slice(0, 4)
    : [
        { word: 'Pragmatic', definition: 'Dealing with things realistically' },
        { word: 'Ambiguous', definition: 'Open to more than one interpretation' },
        { word: 'Ephemeral', definition: 'Lasting for a very short time' },
        { word: 'Resilience', definition: 'Capacity to recover quickly' },
      ]

  return (
    <AppLayout>
      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* ── Hero Bento ── */}
          <div className="bento-12col" style={{ marginBottom: '3rem' }}>
            {/* Main headline card */}
            <div style={{
              padding: '2.5rem',
              borderRadius: '1.5rem',
              background: '#eef0ff',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '18rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
              className="hero-main-card bento-col-12"
            >
              {/* Subtle background glow */}
              <div style={{
                position: 'absolute', top: 0, right: 0, width: '50%', height: '100%',
                background: 'linear-gradient(135deg, transparent, rgba(0,75,226,0.07))',
                pointerEvents: 'none',
              }} />
              <div style={{ position: 'absolute', bottom: '-3rem', right: '-3rem', width: '16rem', height: '16rem', borderRadius: '50%', background: 'rgba(128,155,255,0.15)', filter: 'blur(40px)', pointerEvents: 'none' }} />

              <H style={{ display: 'block', fontSize: 'clamp(3rem, 6vw, 5rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.92, marginBottom: '1.5rem', position: 'relative' }}>
                Level up<br /><span style={{ color: '#004be2', fontStyle: 'italic' }}>your hustle.</span>
              </H>
              <p style={{ color: '#535b71', fontSize: '1.0625rem', lineHeight: 1.6, maxWidth: '28rem', marginBottom: '2rem', fontWeight: 500, position: 'relative' }}>
                {todayStatus?.completed
                  ? t('dashboard.sessionDone')
                  : !hasLearningProfile
                    ? <>Hãy hoàn tất bài đánh giá đầu vào để AI tạo lộ trình cá nhân hoá cho bạn.</>
                    : forecastLevel && targetLevel
                      ? <>{t('dashboard.journeyGood')} <span style={{ color: '#8e00d5', fontWeight: 700 }}>{t('dashboard.forecastLabel')} {forecastLevel}</span> · <span style={{ color: '#004be2', fontWeight: 700 }}>{t('dashboard.targetLabel')} {targetLevel}</span></>
                      : <>{t('dashboard.journeyGood')} <span style={{ color: '#8e00d5', fontWeight: 700 }}>{t('dashboard.level')} {displayLevel}</span></>
                }
              </p>
              {!todayStatus?.completed && hasLearningProfile && focusArea && (
                <p style={{ position: 'relative', color: '#535b71', fontSize: '0.9375rem', marginBottom: '1.5rem', fontWeight: 500 }}>
                  {t('dashboard.todayFocus')}{' '}
                  <span style={{ color: '#8e00d5', fontWeight: 700 }}>{focusArea}</span>
                </p>
              )}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => navigate(!hasLearningProfile ? '/onboarding' : '/session')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
                    padding: '1.125rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #004be2, #809bff)',
                    color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1.0625rem',
                    boxShadow: '0 8px 24px rgba(0,75,226,0.3)', transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,75,226,0.4)' }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,75,226,0.3)' }}
                >
                  {!hasLearningProfile
                    ? 'Làm bài đánh giá đầu vào'
                    : todayStatus?.completed ? t('dashboard.viewProgress') : t('dashboard.startSession')}
                  <span className="material-symbols-outlined" style={{ fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>
                    {!hasLearningProfile ? 'quiz' : todayStatus?.completed ? 'leaderboard' : 'play_arrow'}
                  </span>
                </button>
              </div>
            </div>

            {/* Streak Card */}
            <div style={{
              padding: '2rem',
              borderRadius: '1.5rem',
              background: '#d1dcff',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '10rem',
            }}
              className="streak-bento-card bento-col-6"
            >
              <div style={{ position: 'absolute', bottom: '-2rem', right: '-2rem', opacity: 0.06 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '10rem', fontVariationSettings: "'FILL' 1" }}>local_fire_department</span>
              </div>
              <div>
                <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{t('dashboard.activeStreak')}</H>
                <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>{t('dashboard.dontBreakStreak')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                <H style={{ fontSize: '4rem', fontWeight: 900, color: '#8e00d5', lineHeight: 1 }}>{streak}</H>
                <H style={{ fontSize: '1.25rem', fontWeight: 700, color: '#8e00d5', marginBottom: '0.5rem' }}>{t('dashboard.days')}</H>
                <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#8e00d5', fontVariationSettings: "'FILL' 1", marginBottom: '0.25rem' }}>local_fire_department</span>
              </div>
              {dashboard?.streak?.freezeCount > 0 && (
                <button
                  onClick={() => freeze.mutate()}
                  disabled={freeze.isPending}
                  style={{
                    marginTop: '0.75rem', padding: '0.625rem 1.25rem', borderRadius: '9999px',
                    background: 'rgba(0,75,226,0.1)', border: 'none', cursor: 'pointer',
                    color: '#004be2', fontWeight: 700, fontSize: '0.8125rem',
                    alignSelf: 'flex-start',
                  }}
                >
                  🧊 {t('dashboard.freeze')} ({dashboard.streak.freezeCount})
                </button>
              )}
            </div>

            {/* Exam Countdown */}
            <div style={{
              padding: '2rem',
              borderRadius: '1.5rem',
              background: '#060e20',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '10rem',
            }}
              className="exam-bento-card bento-col-6"
            >
              <div>
                <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, opacity: 0.8, marginBottom: '0.25rem' }}>{t('dashboard.vstepExam')}</H>
                <span style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#809bff' }}>{t('dashboard.countdownLabel')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginTop: '1rem' }}>
                <H style={{ fontSize: '3.5rem', fontWeight: 900, letterSpacing: '-0.04em' }}>
                  {examCountdown != null ? String(examCountdown).padStart(2, '0') : '--'}
                </H>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, opacity: 0.6 }}>{t('dashboard.daysRemaining')}</span>
              </div>
              <div style={{ width: '100%', background: 'rgba(255,255,255,0.1)', height: '0.375rem', borderRadius: '9999px', marginTop: '1rem', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '9999px',
                  background: 'linear-gradient(90deg, #004be2, #809bff)',
                  width: examCountdown != null ? `${Math.min(100, Math.max(10, 100 - (examCountdown / 365) * 100))}%` : '80%',
                }} />
              </div>
            </div>
          </div>

          {/* ── LME-4 Progress Momentum ── */}
          {momentum?.enabled && <MomentumCard m={momentum} />}

          {/* ── Dashboard Grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }} className="dashboard-bottom-grid">
            {/* Weekly Performance */}
            <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '2.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2.5rem' }}>
                <div>
                  <H style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.375rem' }}>{t('dashboard.weeklyPerformance')}</H>
                  <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>{t('dashboard.weeklySubtitle')}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button style={{ background: '#d1dcff', padding: '0.5rem 1rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, color: '#272e42' }}>{t('dashboard.week')}</button>
                  <button style={{ background: 'transparent', padding: '0.5rem 1rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, color: '#535b71' }}>{t('dashboard.month')}</button>
                </div>
              </div>

              {/* Bar chart */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', height: '12rem', alignItems: 'flex-end', padding: '0 1rem' }}>
                {['listening', 'reading', 'writing', 'speaking'].map(skill => {
                  const score = skillScores[skill]
                  const heightPct = score != null ? `${Math.min(100, (score / 10) * 100)}%` : SKILL_HEIGHTS[skill]
                  const isCritical = weakSkills.includes(skill) || (score != null && score < 5)
                  return (
                    <div
                      key={skill}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', height: '100%', justifyContent: 'flex-end' }}
                      className="bar-col"
                    >
                      <div style={{
                        width: '100%', borderRadius: '0.75rem 0.75rem 0 0',
                        background: isCritical ? 'rgba(142,0,213,0.2)' : 'rgba(0,75,226,0.2)',
                        height: heightPct,
                        position: 'relative',
                        transition: 'background 0.3s',
                        cursor: 'pointer',
                      }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = isCritical ? '#8e00d5' : '#004be2'
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = isCritical ? 'rgba(142,0,213,0.2)' : 'rgba(0,75,226,0.2)'
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: '-2rem', left: '50%', transform: 'translateX(-50%)',
                          background: '#d1dcff', padding: '0.2rem 0.5rem', borderRadius: '0.375rem',
                          fontSize: '0.6875rem', fontWeight: 700, whiteSpace: 'nowrap',
                          opacity: 0, transition: 'opacity 0.2s',
                        }}
                          className="bar-tooltip"
                        >
                          {score != null ? `${score.toFixed(1)}/10` : heightPct}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.6875rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#535b71' }}>
                        {SKILL_LABELS[skill]}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', gap: '2rem', marginTop: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', background: '#004be2' }} />
                  <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>{t('dashboard.normalProgress')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', background: '#8e00d5' }} />
                  <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>{t('dashboard.needFocus')}</span>
                </div>
              </div>
            </div>

            {/* Review Deck */}
            <div style={{ background: '#d1dcff', borderRadius: '1.5rem', padding: '2.5rem', display: 'flex', flexDirection: 'column' }}>
              <H style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>{t('dashboard.reviewDeck')}</H>
              <p style={{ fontSize: '0.8125rem', color: '#535b71', marginBottom: '2rem' }}>{t('dashboard.reviewSubtitle')}</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                {reviewWords.map((item: any, i: number) => (
                  <div
                    key={i}
                    onClick={() => navigate('/vocab')}
                    style={{
                      padding: '1rem 1.25rem', background: 'white', borderRadius: '1rem',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      cursor: 'pointer', transition: 'transform 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(4px)' }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none' }}
                  >
                    <div>
                      <H style={{ display: 'block', fontWeight: 700, color: '#004be2', marginBottom: '0.25rem' }}>
                        {item.word ?? item.term}
                      </H>
                      <span style={{ fontSize: '0.75rem', color: '#535b71', fontStyle: 'italic' }}>
                        {item.contextSentence ?? item.definition ?? item.meaning}
                      </span>
                    </div>
                    <span className="material-symbols-outlined" style={{ color: '#a5adc6', fontSize: '1.25rem' }}>chevron_right</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/vocab')}
                style={{
                  marginTop: '1.5rem', padding: '1rem', borderRadius: '9999px',
                  border: '2px solid rgba(0,75,226,0.2)', background: 'transparent',
                  color: '#004be2', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,75,226,0.05)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
              >
                {t('dashboard.viewAllVocab')} ({dashboard?.vocabulary?.total ?? 42})
              </button>
            </div>
          </div>

          {/* ── Quick actions row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))', gap: '1rem', marginTop: '2rem' }}>
            {[
              { icon: 'headphones', label: t('dashboard.practiceListening'), path: '/listening', color: '#006571', bg: '#e6fafb' },
              { icon: 'menu_book', label: t('dashboard.practiceReading'), path: '/reading', color: '#004be2', bg: '#e8eeff' },
              { icon: 'edit_note', label: t('dashboard.practiceWriting'), path: '/writing', color: '#8e00d5', bg: '#f5e6ff' },
              { icon: 'mic', label: t('dashboard.practiceSpeaking'), path: '/speaking', color: '#b41340', bg: '#ffe8ed' },
              { icon: 'emoji_events', label: t('dashboard.challenge'), path: '/challenges', color: '#b45309', bg: '#fef3c7' },
            ].map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  padding: '1.25rem', borderRadius: '1.25rem', border: 'none', cursor: 'pointer',
                  background: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)', transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)' }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)' }}
              >
                <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.875rem', background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="material-symbols-outlined" style={{ color: item.color, fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
                </div>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#272e42' }}>{item.label}</span>
              </button>
            ))}
          </div>

          {/* ── Difficulty Zone Card (FR-006) — only shown when data available ── */}
          {difficultyData && (() => {
            const zoneConfig = {
              flow: { label: t('dashboard.difficultyZone.flow'), color: '#006571', bg: '#e6fafb', icon: 'bolt', tip: t('dashboard.difficultyZone.flowTip') },
              too_hard: { label: t('dashboard.difficultyZone.tooHard'), color: '#b41340', bg: '#ffe8ed', icon: 'sentiment_dissatisfied', tip: t('dashboard.difficultyZone.tooHardTip') },
              too_easy: { label: t('dashboard.difficultyZone.tooEasy'), color: '#b45309', bg: '#fef3c7', icon: 'sentiment_very_satisfied', tip: t('dashboard.difficultyZone.tooEasyTip') },
            }[difficultyData.zone]
            return (
              <div style={{
                marginTop: '1.5rem', padding: '1.25rem 1.5rem', borderRadius: '1.25rem',
                background: zoneConfig.bg, display: 'flex', alignItems: 'center', gap: '1rem',
              }}>
                <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.875rem', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span className="material-symbols-outlined" style={{ color: zoneConfig.color, fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>{zoneConfig.icon}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <H style={{ display: 'block', fontWeight: 800, color: zoneConfig.color, fontSize: '0.9375rem' }}>{zoneConfig.label}</H>
                  <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>
                    {t('dashboard.difficultyZone.successRate')} <strong>{Math.round(difficultyData.successRate * 100)}%</strong> · {t('dashboard.difficultyZone.level')} {difficultyData.currentDifficultyLevel}/7
                    {difficultyData.adjustment !== 0 && (
                      <span style={{ marginLeft: '0.5rem', color: zoneConfig.color, fontWeight: 700 }}>
                        {difficultyData.adjustment > 0 ? t('dashboard.difficultyZone.increase') : t('dashboard.difficultyZone.decrease')}
                      </span>
                    )}
                  </span>
                  <p style={{ fontSize: '0.75rem', color: '#a5adc6', margin: '0.25rem 0 0' }}>{zoneConfig.tip}</p>
                </div>
              </div>
            )
          })()}        </>
      )}
    </AppLayout>
  )
}

// ─── LME-4: Progress Momentum card ──────────────────────────────────────────────
// Rewards tied to MEASURED improvement (accuracy last 14d vs prior 14d), not
// vanity streak counts. Estimated score change is clearly labelled as an estimate.
function MomentumCard({ m }: { m: any }) {
  if (!m.hasData) {
    return (
      <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.75rem', marginBottom: '2rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ width: '3rem', height: '3rem', borderRadius: '1rem', background: '#eef0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontVariationSettings: "'FILL' 1" }}>trending_up</span>
        </div>
        <div>
          <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', color: '#272e42' }}>Đà tiến bộ</H>
          <span style={{ fontSize: '0.875rem', color: '#535b71' }}>Luyện thêm vài buổi nữa để hệ thống đo được tiến bộ thật của bạn.</span>
        </div>
      </div>
    )
  }

  const up = m.trend === 'up'
  const down = m.trend === 'down'
  const accent = up ? '#0f9d58' : down ? '#b41340' : '#004be2'
  const accentBg = up ? 'rgba(15,157,88,0.08)' : down ? 'rgba(180,19,64,0.08)' : '#eef0ff'
  const deltaStr = (n: number | null) => (n == null ? '' : n > 0 ? `+${n}` : `${n}`)
  const headline = up
    ? '🚀 Bạn đang tiến bộ rõ rệt!'
    : down
      ? 'Đừng nản — hãy giữ nhịp luyện tập'
      : 'Bạn đang giữ phong độ ổn định'

  const tiles = [
    { label: 'Độ chính xác 14 ngày', value: `${m.accuracyRecent ?? 0}%`, delta: m.accuracyDelta != null ? `${deltaStr(m.accuracyDelta)} điểm %` : null },
    { label: 'Điểm ước tính', value: m.estScore != null ? `${m.estScore}/10` : '—', delta: m.estScoreDelta != null ? `${deltaStr(m.estScoreDelta)}` : null, sub: m.estLevel ? `≈ ${m.estLevel}` : undefined },
    { label: 'Buổi học (14 ngày)', value: m.sessionsRecent ?? 0 },
    { label: 'Chuỗi ngày', value: m.currentStreak ?? 0 },
  ]

  return (
    <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.75rem', marginBottom: '2rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.875rem', background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span className="material-symbols-outlined" style={{ color: accent, fontVariationSettings: "'FILL' 1" }}>
            {up ? 'rocket_launch' : down ? 'trending_down' : 'trending_flat'}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: '12rem' }}>
          <div style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: accent }}>Đà tiến bộ · 14 ngày</div>
          <H style={{ display: 'block', fontWeight: 900, fontSize: '1.125rem', color: '#272e42' }}>{headline}</H>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))', gap: '0.875rem' }}>
        {tiles.map((tl) => (
          <div key={tl.label} style={{ background: '#f8f9ff', borderRadius: '1rem', padding: '1rem' }}>
            <H style={{ display: 'block', fontSize: '1.5rem', fontWeight: 900, color: '#272e42' }}>
              {tl.value}
              {tl.sub && <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#8e00d5', marginLeft: '0.375rem' }}>{tl.sub}</span>}
            </H>
            <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>{tl.label}</span>
            {tl.delta && (
              <div style={{ marginTop: '0.375rem', fontSize: '0.75rem', fontWeight: 800, color: tl.delta.startsWith('+') ? '#0f9d58' : tl.delta.startsWith('-') ? '#b41340' : '#a5adc6' }}>
                {tl.delta} so với kỳ trước
              </div>
            )}
          </div>
        ))}
      </div>
      <p style={{ fontSize: '0.75rem', color: '#a5adc6', margin: '1rem 0 0', lineHeight: 1.5 }}>
        * "Điểm ước tính" suy ra từ độ chính xác luyện tập thực tế (không phải điểm thi chính thức) — dùng để theo dõi xu hướng tiến bộ.
      </p>
    </div>
  )
}
